/**
 * RecordPreviewCard — renders the CRM record a row would create/update, using
 * the exact same content cards the proposal surface uses (ContactCardContent,
 * DealCardContent, … via ENTITY_CONTENT_REGISTRY), with GenericEntityContent as
 * the fallback for custom entity types. The row's mapped values are passed as
 * `flatProps`, so a not-yet-created record still shows its fields — the same way
 * a "create" action card previews a proposed entity.
 */

import {
  ENTITY_CONTENT_REGISTRY,
  type EntityContentComponentProps,
  isSelfBorderedEntityContent,
} from '@/components/proposals/CreateActionCard.helpers';
import { GenericEntityContent } from '@/components/proposals/GenericEntityContent';
import { computeObjectLabel } from '@/components/proposals/shared/utils';
import { ObjectCard } from '@/lib/crm/object-cards/ObjectCard';

interface RecordPreviewCardProps {
  entityType: string;
  /** The row's mapped property values, keyed by property_type_name. */
  data: Record<string, unknown>;
  orgId: number;
}

export function RecordPreviewCard({
  entityType,
  data,
  orgId,
}: RecordPreviewCardProps) {
  const ContentComponent:
    | React.ComponentType<EntityContentComponentProps>
    | undefined = ENTITY_CONTENT_REGISTRY[entityType];

  if (ContentComponent) {
    if (isSelfBorderedEntityContent(entityType, orgId)) {
      // Self-bordered registered content draws its own card border —
      // wrapping it here would double-nest the border.
      return (
        <ContentComponent
          flatProps={data}
          enrichedData={undefined}
          orgId={orgId}
        />
      );
    }
    // Registered content components are bare (no card chrome); wrap them the
    // same way the proposal surface does.
    return (
      <ObjectCard bordered>
        <ContentComponent
          flatProps={data}
          enrichedData={undefined}
          orgId={orgId}
        />
      </ObjectCard>
    );
  }

  const label =
    computeObjectLabel(data, entityType) ||
    `New ${entityType.replace(/^ahoy_/, '')}`;
  // GenericEntityContent renders its own ObjectCard.
  return (
    <GenericEntityContent
      entityType={entityType}
      label={label}
      flatProps={data}
      orgId={orgId}
    />
  );
}
